import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Plus, Webhook, Copy, RotateCw, Trash2, ChevronDown, ChevronUp, Check, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useRotateWebhookSecret,
  useDeleteWebhookEndpoint,
  useWebhookEvents,
  type WebhookEndpoint,
  type WebhookType,
} from "@/hooks/useWebhookEndpoints";
import { SUPABASE_URL } from "@/lib/supabase";
import { formatError } from "@/lib/utils";

const TYPE_LABELS: Record<WebhookType, { label: string; description: string }> = {
  "hp-tickets": {
    label: "HP Tickets",
    description: "HELLA Projects ticket webhooks (HP outbound format).",
  },
  "linear-issue": {
    label: "Linear",
    description: "Linear issue webhooks (create/update/remove on Issue type).",
  },
  "jira-issue": {
    label: "Jira",
    description: "Jira Cloud issue webhooks (issue_created/_updated/_deleted).",
  },
  "asana-task": {
    label: "Asana",
    description: "Asana task events (added/changed/removed/deleted on tasks).",
  },
  "generic-json": {
    label: "Generic JSON",
    description: "Any JSON payload — define field paths in mapper config.",
  },
};

function buildWebhookUrl(secret: string): string {
  return `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/inbound-webhook?token=${secret}`;
}

function copyToClipboard(text: string, label = "URL") {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Copy failed"));
}

function AddWebhookSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState<WebhookType>("generic-json");
  const [genericIdPath, setGenericIdPath] = useState("data.id");
  const [genericTitlePath, setGenericTitlePath] = useState("data.title");
  const [genericNotesPath, setGenericNotesPath] = useState("data.description");
  const [genericUrlPath, setGenericUrlPath] = useState("data.url");
  const [genericSourcePrefix, setGenericSourcePrefix] = useState("custom");
  const create = useCreateWebhookEndpoint();
  const [createdEndpoint, setCreatedEndpoint] = useState<WebhookEndpoint | null>(null);

  const reset = () => {
    setName("");
    setType("generic-json");
    setGenericIdPath("data.id");
    setGenericTitlePath("data.title");
    setGenericNotesPath("data.description");
    setGenericUrlPath("data.url");
    setGenericSourcePrefix("custom");
    setCreatedEndpoint(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    const config =
      type === "generic-json"
        ? {
            id_path: genericIdPath.trim(),
            title_path: genericTitlePath.trim(),
            notes_path: genericNotesPath.trim() || undefined,
            url_path: genericUrlPath.trim() || undefined,
            source_prefix: genericSourcePrefix.trim() || "custom",
          }
        : {};
    try {
      const endpoint = await create.mutateAsync({ name: name.trim(), type, mapper_config: config });
      setCreatedEndpoint(endpoint);
    } catch (err) {
      toast.error(`Create failed: ${formatError(err)}`);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{createdEndpoint ? "Webhook created" : "Add webhook"}</SheetTitle>
          <SheetDescription>
            {createdEndpoint
              ? "Copy the URL into your upstream system. The secret token is only shown here — rotate it from the list if you lose it."
              : "Drop the resulting URL into your upstream system's webhook config. Items land in your HO inbox."}
          </SheetDescription>
        </SheetHeader>

        {createdEndpoint ? (
          <div className="mt-4 space-y-3">
            <Label className="text-xs text-muted-foreground">Webhook URL</Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={buildWebhookUrl(createdEndpoint.secret_token)}
                className="font-mono text-xs"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => copyToClipboard(buildWebhookUrl(createdEndpoint.secret_token))}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Type: <span className="font-mono">{createdEndpoint.type}</span>
            </p>
            <Button onClick={handleClose} className="w-full mt-4">
              Done
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Linear workspace"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as WebhookType)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>
                      {meta.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{TYPE_LABELS[type].description}</p>
            </div>

            {type === "generic-json" && (
              <div className="space-y-3 border-l-2 border-border pl-3 mt-2">
                <p className="text-xs text-muted-foreground">
                  Dot-path syntax. For payload <span className="font-mono">{"{data:{id:1,title:'x'}}"}</span>{" "}
                  use <span className="font-mono">data.id</span> /{" "}
                  <span className="font-mono">data.title</span>.
                </p>
                <div>
                  <Label className="text-xs">ID path (required)</Label>
                  <Input value={genericIdPath} onChange={(e) => setGenericIdPath(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Title path (required)</Label>
                  <Input value={genericTitlePath} onChange={(e) => setGenericTitlePath(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Notes path</Label>
                  <Input value={genericNotesPath} onChange={(e) => setGenericNotesPath(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">URL path</Label>
                  <Input value={genericUrlPath} onChange={(e) => setGenericUrlPath(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Source prefix</Label>
                  <Input
                    value={genericSourcePrefix}
                    onChange={(e) => setGenericSourcePrefix(e.target.value)}
                    className="mt-1 font-mono text-xs"
                    placeholder="custom"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used in source_ref e.g. {genericSourcePrefix}:123.
                  </p>
                </div>
              </div>
            )}

            <Button onClick={handleSubmit} disabled={create.isPending || !name.trim()} className="w-full">
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create webhook
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EndpointRow({ endpoint }: { endpoint: WebhookEndpoint }) {
  const [expanded, setExpanded] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const update = useUpdateWebhookEndpoint();
  const rotate = useRotateWebhookSecret();
  const del = useDeleteWebhookEndpoint();
  const { data: events } = useWebhookEvents(expanded ? endpoint.id : null);

  const meta = TYPE_LABELS[endpoint.type];
  const webhookUrl = buildWebhookUrl(endpoint.secret_token);

  const handleRotate = async () => {
    if (!confirm("Rotate the secret token? The old URL stops working immediately.")) return;
    try {
      await rotate.mutateAsync(endpoint.id);
      toast.success("Secret rotated");
      setShowSecret(true);
    } catch (e) {
      toast.error(`Rotate failed: ${String(e)}`);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${endpoint.name}"? This stops accepting events from this URL.`)) return;
    try {
      await del.mutateAsync(endpoint.id);
      toast.success("Webhook deleted");
    } catch (e) {
      toast.error(`Delete failed: ${String(e)}`);
    }
  };

  return (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{endpoint.name}</span>
            <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
            {!endpoint.enabled && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                Disabled
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {endpoint.event_count} event{endpoint.event_count === 1 ? "" : "s"}
            {endpoint.last_event_at
              ? ` · last ${formatDistanceToNow(new Date(endpoint.last_event_at), { addSuffix: true })}`
              : " · never received"}
          </p>
          {endpoint.last_error && (
            <p className="text-xs text-destructive mt-0.5 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {endpoint.last_error.slice(0, 80)}
            </p>
          )}
        </div>
        <Switch
          checked={endpoint.enabled}
          onCheckedChange={(checked) => update.mutate({ id: endpoint.id, enabled: checked })}
        />
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-border">
          <div>
            <Label className="text-xs">Webhook URL</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                readOnly
                type={showSecret ? "text" : "password"}
                value={webhookUrl}
                className="font-mono text-xs"
              />
              <Button size="icon" variant="outline" onClick={() => setShowSecret((v) => !v)}>
                {showSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button size="icon" variant="outline" onClick={() => copyToClipboard(webhookUrl)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={handleRotate} disabled={rotate.isPending}>
              <RotateCw className="h-3 w-3 mr-1" />
              Rotate secret
            </Button>
            <Button size="sm" variant="outline" onClick={handleDelete} disabled={del.isPending}>
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>
          </div>

          {events && events.length > 0 && (
            <div>
              <Label className="text-xs">Recent events</Label>
              <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
                {events.map((ev) => (
                  <div key={ev.id} className="text-xs flex items-center gap-2 py-1 border-b border-border last:border-0">
                    <Badge
                      variant="outline"
                      className={`text-[9px] ${
                        ev.status === "ok"
                          ? "text-green-500"
                          : ev.status === "error"
                          ? "text-destructive"
                          : "text-muted-foreground"
                      }`}
                    >
                      {ev.status}
                    </Badge>
                    <span className="text-muted-foreground shrink-0">
                      {format(new Date(ev.received_at), "MMM d h:mm a")}
                    </span>
                    <span className="truncate flex-1">
                      {ev.payload_summary || (ev.error ? `error: ${ev.error}` : "(no summary)")}
                    </span>
                    {ev.items_created > 0 && (
                      <span className="text-muted-foreground shrink-0">+{ev.items_created}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function WebhooksSection() {
  const { data: endpoints, isLoading } = useWebhookEndpoints();
  const [adding, setAdding] = useState(false);

  const items = useMemo(() => endpoints || [], [endpoints]);

  return (
    <section className="mb-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Webhook className="h-4 w-4" />
                Webhooks
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Pipe external systems (Linear, Jira, Asana, HP, generic JSON) into your inbox.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              No webhooks yet. Click <span className="font-medium">Add</span> to create one.
            </p>
          ) : (
            items.map((ep) => <EndpointRow key={ep.id} endpoint={ep} />)
          )}
        </CardContent>
      </Card>
      <AddWebhookSheet open={adding} onClose={() => setAdding(false)} />
    </section>
  );
}
